/**
 * W3C Trace Context utilities for distributed tracing.
 *
 * This module provides functions to inject and extract W3C Trace Context
 * (traceparent/tracestate headers) for propagating traces across services.
 */

import { propagation, context } from '@opentelemetry/api';

/**
 * Injects W3C Trace Context into a carrier object for distributed tracing.
 *
 * This allows traces to continue across service boundaries by propagating
 * the trace context via the traceparent header.
 *
 * @returns Carrier object with traceparent (and optionally tracestate)
 *
 * @example
 * const carrier = injectTraceContext();
 * // carrier = { traceparent: "00-4bf92f3577b34da6a3ce929d0e0e4736-00f067aa0ba902b7-01" }
 *
 * // Send this carrier as part of your message to the server
 * const message = {
 *   action: "api.sfu.subscriber.offer",
 *   payload: { sdp: "..." },
 *   trace_context: carrier
 * };
 */
export function injectTraceContext(): Record<string, string> {
  const carrier: Record<string, string> = {};

  // Inject the current trace context into the carrier
  propagation.inject(context.active(), carrier);

  return carrier;
}

/**
 * Extracts W3C Trace Context from a carrier object and returns a context.
 *
 * This allows the service to continue an existing trace that was started
 * by a client or another service.
 *
 * @param carrier - Object containing traceparent (and optionally tracestate)
 * @returns OpenTelemetry context with the extracted trace information
 *
 * @example
 * const carrier = { traceparent: "00-4bf92f3577b34da6a3ce929d0e0e4736-00f067aa0ba902b7-01" };
 * const ctx = extractTraceContext(carrier);
 *
 * // Use this context to continue the trace
 * const span = trace.getTracer('my-service').startSpan('operation', undefined, ctx);
 */
export function extractTraceContext(carrier: Record<string, string | undefined>) {
  // Extract trace context from carrier
  return propagation.extract(context.active(), carrier);
}

/**
 * Gets the traceparent header from the current active span context.
 *
 * This is useful for manually adding trace context to messages or logs.
 *
 * @returns The traceparent string or null if no active span
 *
 * @example
 * const traceparent = getActiveTraceparent();
 * // Returns: "00-4bf92f3577b34da6a3ce929d0e0e4736-00f067aa0ba902b7-01"
 */
export function getActiveTraceparent(): string | null {
  const carrier: Record<string, string> = {};
  propagation.inject(context.active(), carrier);
  return carrier.traceparent || null;
}
